import '@fontsource-variable/jura';
import { ArrowLeft, RotateCcw } from 'lucide-react';
import { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  KanameIntro,
  type IntroMode,
  type IntroPhase,
  type KanameIntroHandle,
} from '@/components/brand/KanameIntro';
import { KanameMark } from '@/components/brand/KanameMark';
import { KanameNote } from '@/components/brand/KanameNote';
import { KanameWordmark, WORDMARK_DOT_MS } from '@/components/brand/KanameWordmark';
import { LeafGround } from '@/components/brand/LeafGround';
import googleG from '@/components/brand/google-g.svg';
import { prefersReducedMotion, saveEntrance } from '@/components/layout/shell/entrance';
import { Spinner } from '@/components/Spinner';
import { Tabs, TabsList, TabsTrigger, cn } from '@/components/ui';
import './login.css';

/*
  /design/login — страница входа по концепции Kaname (docs/brand-concept.md §3–5), раскладка как у
  incident.io: слева сцена, справа форма. Вход имитирован: кнопка не ведёт в Google, а сразу
  разыгрывает переход в рабочее место («Основа»), чтобы его можно было крутить сколько угодно.
  Сверху слева — переключатели лаборатории; на настоящем входе их не будет.

  Сцена: одно ядро → касание → веер с 6 часов по часовой (последние лепестки садятся всё медленнее),
  вместе с лепестками проявляются буквы KANAME, следом выскакивает точка-ядро и выходит слоган по словам
  → веер стоит → лепестки медленно темнеют в окна, за ними проявляется листопад. Когда всё
  улеглось, время от времени лист выпадает из окна на землю внизу сцены; мышью листья можно
  разбросать. Каждый вход — полное знакомство в одном темпе. Когда всё улеглось, внизу проявляется
  сноска — что значит kaname; её звёздочка — та же коралловая точка, что после слова KANAME.

  Шрифты: слово KANAME — Noto Sans JP, как в меню; весь русский текст — Jura.
*/

const SLOGAN = ['Всё', 'начинается', 'с', 'центра'];
/** Слоган выходит сразу за точкой слова (задержки — от начала раскрытия); слова — мягкой волной. */
const SLOGAN_FROM_MS = WORDMARK_DOT_MS + 600;
const WORD_STEP_MS = 160;

export function LoginLab() {
  const navigate = useNavigate();
  const intro = useRef<KanameIntroHandle>(null);
  const stageRef = useRef<HTMLElement>(null);
  const markRef = useRef<HTMLDivElement>(null);
  const [mode, setMode] = useState<IntroMode>(() => (prefersReducedMotion() ? 'still' : 'first'));
  const [phase, setPhase] = useState<IntroPhase>('core');
  const [signing, setSigning] = useState(false);

  const signIn = () => {
    const el = markRef.current;
    if (!el || signing) return;
    setSigning(true);
    const r = el.getBoundingClientRect();
    const depth = intro.current?.isDepth() ?? false;
    saveEntrance({
      x: r.left + r.width / 2,
      y: r.top + r.height / 2,
      size: r.width,
      variant: depth ? 'window' : 'solid',
    });
    // Пауза — как короткий поход в Google и обратно.
    window.setTimeout(() => navigate('/design/foundation'), 520);
  };

  const still = mode === 'still';
  const wordPlay = still ? 'still' : phase === 'core' ? 'hidden' : 'play';
  // Слоган идёт сразу за словом и точкой, не дожидаясь затемнения окон (задержки — от начала раскрытия).
  const sloganShown = still || phase !== 'core';

  return (
    <div className="flex h-screen flex-col overflow-y-auto bg-night text-petal md:flex-row">
      <section
        ref={stageRef}
        data-login-stage=""
        className="relative flex min-h-96 flex-3 flex-col items-center justify-center gap-8 px-8 pt-16 pb-32"
      >
        <LeafGround stageRef={stageRef} markRef={markRef} active={phase === 'settled' && !still} />

        <div className="absolute top-4 left-4 z-raised flex max-w-full flex-wrap items-center gap-3 pr-4 text-xs">
          <Link to="/design" className="inline-flex items-center gap-1.5 text-petal/60 hover:text-petal">
            <ArrowLeft className="size-3.5" />
            Галерея
          </Link>
          <Tabs variant="pill" value={mode} onValueChange={(v) => setMode(v as IntroMode)}>
            <TabsList>
              <TabsTrigger value="first">Знакомство</TabsTrigger>
              <TabsTrigger value="still">Без движения</TabsTrigger>
            </TabsList>
          </Tabs>
          <button
            type="button"
            onClick={() => {
              setMode('first');
              intro.current?.replay();
            }}
            className="inline-flex items-center gap-1.5 text-petal/60 hover:text-petal"
          >
            <RotateCcw className="size-3.5" />
            Повторить знакомство
          </button>
        </div>

        <div ref={markRef} data-login-mark="">
          <KanameIntro ref={intro} mode={mode} key={mode} onPhaseChange={setPhase} />
        </div>

        <KanameWordmark size="stage" play={wordPlay} key={mode} />

        <div
          className={cn('flex flex-col items-center gap-3 text-center', !sloganShown && 'invisible')}
          key={`${mode}-${sloganShown}`}
        >
          <p data-slogan="">
            {SLOGAN.map((w, i) => (
              <span key={i}>
                <span
                  data-word-in={!still || undefined}
                  style={still ? undefined : { animationDelay: `${SLOGAN_FROM_MS + i * WORD_STEP_MS}ms` }}
                >
                  {w}
                </span>
                {i < SLOGAN.length - 1 && ' '}
              </span>
            ))}
          </p>
          <p
            data-slogan-sub=""
            data-sub-in={!still || undefined}
            style={
              still
                ? undefined
                : { animationDelay: `${SLOGAN_FROM_MS + SLOGAN.length * WORD_STEP_MS + 250}ms` }
            }
          >
            Один центр. Множество элементов. Единая система.
          </p>
        </div>

        {(still || phase === 'settled') && (
          <KanameNote
            animate={!still}
            className="absolute inset-x-8 bottom-8 z-raised mx-auto max-w-xl text-center"
          />
        )}
      </section>

      <aside
        data-login-copy=""
        className="flex flex-2 flex-col justify-between border-t border-rail-line bg-rail p-10 md:max-w-lg md:border-t-0 md:border-l"
      >
        <div className="flex items-center gap-3">
          <KanameMark className="size-8" />
          <KanameWordmark />
        </div>

        <div className="flex flex-col gap-6 py-12">
          <div className="flex flex-col gap-2">
            <h1 className="type-title text-petal">Войти в Kaname</h1>
            <p className="text-sm leading-6 text-petal/70">
              Для сотрудников демо-компании — по корпоративному аккаунту Google.
            </p>
          </div>
          <button
            type="button"
            onClick={signIn}
            disabled={signing}
            className={cn(
              'flex h-11 w-full items-center justify-center gap-3 rounded-md bg-petal text-sm font-semibold text-night',
              'transition-interactive hover:brightness-95 disabled:cursor-progress disabled:opacity-80',
            )}
          >
            {signing ? <Spinner /> : <img src={googleG} alt="" className="size-4" />}
            {signing ? 'Входим…' : 'Войти через Google'}
          </button>
        </div>

        <p className="text-xs leading-5 text-petal/50">
          Пароль вводится на стороне Google — Kaname его не видит. Доступ к приложениям выдают их владельцы.
        </p>
      </aside>
    </div>
  );
}
